'use client';

import { useRef, useState } from 'react';

import { api } from '../api/client';

import { Button } from './ui/Button';
import { Modal } from './ui/Modal';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  page?: string;
  agentId?: string;
}

export function FeedbackModal({ open, onClose, page, agentId }: FeedbackModalProps) {
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitGuard = useRef(false);

  const handleSubmit = async () => {
    if (!description.trim() || submitGuard.current) return;
    submitGuard.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await api.submitFeedback({
        category,
        description: description.trim(),
        page: page || window.location.pathname,
        agent_id: agentId,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
      submitGuard.current = false;
    }
  };

  const handleClose = () => {
    setDescription('');
    setCategory('bug');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Send Feedback" maxWidth="max-w-lg">
      {submitted ? (
        <div className="text-center py-4">
          <div className="text-2xl mb-2">&#10003;</div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Thanks for your feedback! We&apos;ll look into it.
          </p>
          <Button variant="secondary" onClick={handleClose} className="mt-4">
            Close
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Category
              </label>
              <div className="flex gap-2">
                {(['bug', 'feature', 'other'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-3 py-1.5 text-xs font-mono rounded border transition-colors cursor-pointer ${
                      category === c
                        ? 'border-[#1f2933] dark:border-[#e5e5e5] bg-[#1f2933] dark:bg-[#e5e5e5] text-white dark:text-[#111111]'
                        : 'border-gray-200 dark:border-[#2a2a2a] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#222222]'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What happened? What did you expect?"
                rows={4}
                maxLength={2000}
                className="w-full p-3 border border-gray-200 dark:border-[#2a2a2a] rounded bg-white dark:bg-[#111111] text-sm text-[#1f2933] dark:text-[#e5e5e5] placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0052ff]/40 focus:border-[#0052ff] resize-none"
              />
            </div>

            {agentId && (
              <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">Agent: {agentId}</p>
            )}

            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>

          <div className="flex justify-end gap-3 mt-5">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!description.trim() || submitting}
            >
              {submitting ? 'Sending...' : 'Submit'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function FeedbackButton({ page, agentId }: { page?: string; agentId?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline underline-offset-2 transition-colors cursor-pointer"
      >
        Send Feedback
      </button>
      <FeedbackModal open={open} onClose={() => setOpen(false)} page={page} agentId={agentId} />
    </>
  );
}
